/**
 * One value in the KPI grid.
 *
 * The three states are distinguished QUIETLY — a dotted underline for
 * unverified, a teal dot for overridden — because on a grid of several hundred
 * numbers, a loud treatment of the exceptional ones reads as an error state for
 * the whole table. Quiet has to be explained, so hovering (or focusing) a value
 * opens a card that says what its look means and where it came from (DOP-609).
 * The state is also in the `aria-label`, so it does not depend on colour.
 *
 * Memoized: a property grid is ~20 metrics × 6+ months of leaf cells, and
 * opening the drill-in re-renders the page.
 */

import { Info } from "lucide-react";
import React, { useCallback, useEffect, useId, useRef, useState } from "react";

import { type KpiCell, type KpiUnit, cellState } from "../../types/kpi";
import KpiCellTooltip from "./KpiCellTooltip";
import { formatKpiValue } from "./formatKpiValue";
import { kpiCellAction } from "./kpiCellAction";
import { KPI_CELL_STATE_ARIA, KPI_CELL_STATE_CLASSES } from "./kpiCellStates";

export interface KpiCellViewProps {
	cell: KpiCell | undefined;
	unit: KpiUnit;
	metricKey: string;
	metricLabel: string;
	period: string;
	periodLabel: string;
	selected: boolean;
	/**
	 * Metadata / override / ask-about, on a hover affordance rather than the
	 * cell itself. The primary click belongs to the source document — that is
	 * what a reader wants nine times out of ten — so the secondary action gets a
	 * separate, small target that must never intercept it.
	 */
	onOpenDetails: (metricKey: string, period: string) => void;
	/**
	 * Takes the coordinates rather than being a per-cell closure: the parent can
	 * then hold ONE `useCallback` for the whole grid and every memoized cell
	 * keeps its props stable across a selection change.
	 */
	onSelect: (metricKey: string, period: string) => void;
}

/** Long enough that sweeping the pointer across a row does not strobe cards. */
const HOVER_DELAY_MS = 200;

const KpiCellView: React.FC<KpiCellViewProps> = ({
	cell,
	unit,
	metricKey,
	metricLabel,
	period,
	periodLabel,
	selected,
	onSelect,
	onOpenDetails,
}) => {
	const state = cellState(cell);
	const text =
		state === "empty" ? "—" : formatKpiValue(cell?.effective_value, unit);

	const tooltipId = useId();
	const [anchor, setAnchor] = useState<DOMRect | null>(null);
	const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const clearTimer = () => {
		if (timer.current !== null) clearTimeout(timer.current);
		timer.current = null;
	};
	const show = useCallback((target: HTMLElement, delay: number) => {
		clearTimer();
		timer.current = setTimeout(
			() => setAnchor(target.getBoundingClientRect()),
			delay,
		);
	}, []);
	const hide = useCallback(() => {
		clearTimer();
		setAnchor(null);
	}, []);
	useEffect(() => clearTimer, []);
	// The card is placed from the cell's rect at hover time; a scroll moves
	// the cell out from under it, so the card closes rather than float loose.
	const open = anchor !== null;
	useEffect(() => {
		if (!open) return;
		window.addEventListener("scroll", hide, true);
		window.addEventListener("resize", hide);
		return () => {
			window.removeEventListener("scroll", hide, true);
			window.removeEventListener("resize", hide);
		};
	}, [open, hide]);
	// Resolved only while the card is open: the grid has hundreds of cells and
	// none of them needs its provenance walked until someone points at it.
	const action = anchor ? kpiCellAction(cell) : null;

	return (
		<td
			data-selected={selected || undefined}
			className={`group border-b border-gray-100 px-3 py-1.5 text-right tabular-nums ${
				// The scroll margins clear the sticky header and Metric column,
				// so a cell scrolled into view is not left underneath them.
				selected ? "scroll-ml-[240px] scroll-mt-10 bg-[#E6F2F1]" : ""
			}`}
		>
			<div className="flex items-center justify-end gap-1">
				<button
					type="button"
					onClick={() => {
						hide();
						onSelect(metricKey, period);
					}}
					onMouseEnter={(event) => show(event.currentTarget, HOVER_DELAY_MS)}
					onMouseLeave={hide}
					onFocus={(event) => show(event.currentTarget, 0)}
					onBlur={hide}
					aria-label={`${metricLabel}, ${periodLabel}, ${text}, ${KPI_CELL_STATE_ARIA[state]}`}
					aria-describedby={anchor ? tooltipId : undefined}
					className={`min-w-0 flex-1 rounded px-1 text-right text-base hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500 ${KPI_CELL_STATE_CLASSES[state]}`}
				>
					{text}
					{state === "overridden" && (
						<span aria-hidden="true" className="ml-1">
							•
						</span>
					)}
				</button>
				{anchor && action && (
					<KpiCellTooltip
						id={tooltipId}
						state={state}
						valueText={text}
						sourceFile={action.kind === "open" ? action.source.file : null}
						clickOpensFile={action.kind === "open"}
						anchor={anchor}
					/>
				)}
				{/* The slot is ALWAYS the same size, whether or not it holds a
				    control: revealing the icon on hover must not move the number it
				    sits beside, and a column of values has to stay aligned whether
				    or not a given month has one. */}
				{state === "empty" ? (
					<span className="h-4 w-4 shrink-0" aria-hidden="true" />
				) : (
					<button
						type="button"
						onClick={(event) => {
							// The cell's own handler must not also fire: one click, one
							// action, and this one is explicitly the other action.
							event.stopPropagation();
							onOpenDetails(metricKey, period);
						}}
						aria-label={`Details for ${metricLabel}, ${periodLabel}`}
						// Faded rather than removed: an element hidden with `display` or
						// `visibility` leaves the tab order, and this is the only route
						// to the override for keyboard users. Opacity keeps it
						// focusable, and focus brings it back into view.
						className="flex h-4 w-4 shrink-0 items-center justify-center rounded text-text-muted opacity-0 transition-opacity hover:bg-gray-200 focus:opacity-100 focus:outline-none focus:ring-1 focus:ring-primary-500 group-hover:opacity-100"
					>
						<Info className="h-3 w-3" aria-hidden="true" />
					</button>
				)}
			</div>
		</td>
	);
};

export default React.memo(KpiCellView);
